import { useForm, usePage } from "@inertiajs/react"
import { EyeIcon, EyeOffIcon, PlusIcon, Trash2Icon } from "lucide-react"
import { useState } from "react"

import { Button } from "@/components/ui/button"
import { FieldError } from "@/components/ui/field"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import type { InsideItem, RenderType } from "@/lib/types"
import type { SharedProps } from "@/types"

interface FormData {
  category_id: number
  title: string
  content: string
  cta_label: string
  cta_url: string
  published: boolean
  metadata: Record<string, unknown>
}

interface LinkRow {
  id: string
  label: string
  url: string
}

// ---- Metadata field sets by render_type ----

function CredentialFields({
  metadata,
  onChange,
}: {
  metadata: Record<string, unknown>
  onChange: (meta: Record<string, unknown>) => void
}) {
  const [revealed, setRevealed] = useState(false)
  return (
    <div className="grid gap-3">
      <div className="grid gap-1.5">
        <Label htmlFor="credential-label">Label (e.g. "Network name")</Label>
        <Input
          id="credential-label"
          value={(metadata.label as string) ?? ""}
          onChange={(e) => onChange({ ...metadata, label: e.target.value })}
        />
      </div>
      <div className="grid gap-1.5">
        <Label htmlFor="credential-value">Value (e.g. password)</Label>
        <div className="flex gap-2">
          <Input
            id="credential-value"
            type={revealed ? "text" : "password"}
            value={(metadata.value as string) ?? ""}
            onChange={(e) => onChange({ ...metadata, value: e.target.value })}
            className="font-mono"
            data-testid="credential-value"
          />
          <Button
            type="button"
            variant="outline"
            size="icon"
            onClick={() => setRevealed((r) => !r)}
            aria-label={revealed ? "Hide value" : "Reveal value"}
            data-testid="reveal-toggle"
          >
            {revealed ? <EyeOffIcon className="size-4" /> : <EyeIcon className="size-4" />}
          </Button>
        </div>
      </div>
    </div>
  )
}

const CONTACT_FIELD_TYPES: Record<string, React.HTMLInputTypeAttribute> = {
  name:  "text",
  phone: "tel",
  email: "email",
  hours: "text",
}

function ContactFields({
  metadata,
  onChange,
}: {
  metadata: Record<string, unknown>
  onChange: (meta: Record<string, unknown>) => void
}) {
  return (
    <div className="grid gap-3">
      {(["name", "phone", "email", "hours"] as const).map((field) => (
        <div key={field} className="grid gap-1.5">
          <Label htmlFor={`contact-${field}`} className="capitalize">{field}</Label>
          <Input
            id={`contact-${field}`}
            type={CONTACT_FIELD_TYPES[field]}
            value={(metadata[field] as string) ?? ""}
            onChange={(e) => onChange({ ...metadata, [field]: e.target.value })}
            data-testid={`contact-${field}`}
          />
        </div>
      ))}
    </div>
  )
}

function LinkListFields({
  metadata,
  onChange,
}: {
  metadata: Record<string, unknown>
  onChange: (meta: Record<string, unknown>) => void
}) {
  const links: LinkRow[] = Array.isArray(metadata.links)
    ? (metadata.links as LinkRow[])
    : []

  function updateLink(idx: number, field: keyof Omit<LinkRow, "id">, value: string) {
    const updated = links.map((l, i) => (i === idx ? { ...l, [field]: value } : l))
    onChange({ ...metadata, links: updated })
  }

  function addLink() {
    onChange({ ...metadata, links: [...links, { id: crypto.randomUUID(), label: "", url: "" }] })
  }

  function removeLink(idx: number) {
    onChange({ ...metadata, links: links.filter((_, i) => i !== idx) })
  }

  return (
    <div className="grid gap-2">
      <Label>Links</Label>
      {links.map((link, idx) => (
        <div key={link.id ?? idx} className="flex items-center gap-2">
          <Input
            placeholder="Label"
            value={link.label}
            onChange={(e) => updateLink(idx, "label", e.target.value)}
            data-testid={`link-label-${idx}`}
          />
          <Input
            placeholder="URL"
            type="url"
            value={link.url}
            onChange={(e) => updateLink(idx, "url", e.target.value)}
            data-testid={`link-url-${idx}`}
          />
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="shrink-0 text-destructive"
            onClick={() => removeLink(idx)}
            aria-label={`Remove link ${idx + 1}`}
          >
            <Trash2Icon className="size-4" />
          </Button>
        </div>
      ))}
      <Button type="button" variant="outline" size="sm" onClick={addLink}>
        <PlusIcon className="mr-1.5 size-4" />
        Add link
      </Button>
    </div>
  )
}

interface MetadataFieldsProps {
  renderType: RenderType
  metadata: Record<string, unknown>
  content: string
  onMetaChange: (meta: Record<string, unknown>) => void
  onContentChange: (v: string) => void
}

export function MetadataFields({
  renderType,
  metadata,
  content,
  onMetaChange,
  onContentChange,
}: MetadataFieldsProps) {
  switch (renderType) {
    case "credential":
      return <CredentialFields metadata={metadata} onChange={onMetaChange} />
    case "contact":
      return <ContactFields metadata={metadata} onChange={onMetaChange} />
    case "link_list":
      return <LinkListFields metadata={metadata} onChange={onMetaChange} />
    case "hours":
      return (
        <div className="grid gap-1.5">
          <Label>Opening hours</Label>
          <p className="text-xs text-muted-foreground">
            One entry per line, e.g. "Mon–Fri: 9:00–18:00" or "Closed on Sundays"
          </p>
          <Textarea
            value={content}
            onChange={(e) => onContentChange(e.target.value)}
            rows={6}
            data-testid="content-textarea"
          />
        </div>
      )
    case "rich_block":
    case "text":
    default:
      return (
        <div className="grid gap-1.5">
          <Label>Content</Label>
          <Textarea
            value={content}
            onChange={(e) => onContentChange(e.target.value)}
            rows={4}
            data-testid="content-textarea"
          />
        </div>
      )
  }
}

// ---- Main form ----

interface Props {
  item?: InsideItem
  categoryId: number
  renderType: RenderType
  createUrl: string
  onClose: () => void
}

export function ItemForm({ item, categoryId, renderType, createUrl, onClose }: Props) {
  const { errors } = usePage<SharedProps>().props
  const { data, setData, post, patch, processing, transform } = useForm<FormData>({
    category_id: categoryId,
    title:       item?.title ?? "",
    content:     item?.content ?? "",
    cta_label:   item?.cta_label ?? "",
    cta_url:     item?.cta_url ?? "",
    published:   item?.published ?? true,
    metadata:    item?.metadata ?? {},
  })

  transform((d) => ({ inside_item: d }))

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (item) {
      patch(item.update_url, { onSuccess: onClose })
    } else {
      post(createUrl, { onSuccess: onClose })
    }
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4 p-6">
      <div className="grid gap-1.5">
        <Label htmlFor="item-title">Title *</Label>
        <Input
          id="item-title"
          value={data.title}
          onChange={(e) => setData("title", e.target.value)}
          required
        />
        <FieldError>{errors.title}</FieldError>
      </div>

      <MetadataFields
        renderType={renderType}
        metadata={data.metadata}
        content={data.content}
        onMetaChange={(m) => setData("metadata", m)}
        onContentChange={(v) => setData("content", v)}
      />
      <FieldError>{errors.content}</FieldError>
      <FieldError>{errors.metadata}</FieldError>

      <div className="grid grid-cols-2 gap-3">
        <div className="grid gap-1.5">
          <Label htmlFor="item-cta-label">CTA Label</Label>
          <Input
            id="item-cta-label"
            placeholder="e.g. Book a table"
            value={data.cta_label}
            onChange={(e) => setData("cta_label", e.target.value)}
          />
        </div>
        <div className="grid gap-1.5">
          <Label htmlFor="item-cta-url">CTA URL</Label>
          <Input
            id="item-cta-url"
            type="url"
            value={data.cta_url}
            onChange={(e) => setData("cta_url", e.target.value)}
          />
          <FieldError>{errors.cta_url}</FieldError>
        </div>
      </div>

      <div className="flex items-center gap-2">
        <input
          id="item-published"
          type="checkbox"
          checked={data.published}
          onChange={(e) => setData("published", e.target.checked)}
          className="h-4 w-4 border-border"
        />
        <Label htmlFor="item-published" className="cursor-pointer font-normal">
          Published
        </Label>
      </div>

      <div className="flex justify-end gap-2 pt-2">
        <Button type="button" variant="outline" onClick={onClose}>
          Cancel
        </Button>
        <Button type="submit" disabled={processing}>
          {processing ? "Saving…" : item ? "Save changes" : "Add item"}
        </Button>
      </div>
    </form>
  )
}
